const DEFAULT_DEBOUNCE_MS = 1000;

export const hasLocalMediaInHtml = (html: string) =>
  /(?:src|href)=["'](?:blob:|data:)/i.test(html);

export type ConceptAutosaveDeps = {
  getHtml: () => string;
  persist: (html: string) => void;
  debounceMs?: number;
};

export type ConceptAutosave = {
  scheduleSave: () => void;
  saveNow: () => void;
  pause: () => void;
  resumeAndSave: () => void;
  resetSnapshot: (html: string) => void;
  markSaveSucceeded: (html: string) => void;
  markSaveFailed: () => void;
  dispose: () => void;
};

export function createConceptAutosave(
  deps: ConceptAutosaveDeps
): ConceptAutosave {
  const debounceMs = deps.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  let lastSaved = "";
  let saving = false;
  let paused = false;
  let retryAfterSave = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancelTimer = () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = null;
  };

  const saveNow = () => {
    if (paused) return;
    if (saving) {
      retryAfterSave = true;
      return;
    }

    const html = deps.getHtml();
    if (hasLocalMediaInHtml(html)) return;
    if (html === lastSaved) return;

    saving = true;
    deps.persist(html);
  };

  const scheduleSave = () => {
    if (paused) return;
    if (saving) retryAfterSave = true;
    cancelTimer();
    timer = setTimeout(() => {
      timer = null;
      saveNow();
    }, debounceMs);
  };

  return {
    scheduleSave,
    saveNow,
    pause: () => {
      paused = true;
      cancelTimer();
    },
    resumeAndSave: () => {
      paused = false;
      cancelTimer();
      saveNow();
    },
    resetSnapshot: (html) => {
      lastSaved = html;
      saving = false;
      retryAfterSave = false;
      cancelTimer();
    },
    markSaveSucceeded: (html) => {
      saving = false;
      lastSaved = html;
      if (!retryAfterSave) return;
      retryAfterSave = false;
      scheduleSave();
    },
    markSaveFailed: () => {
      saving = false;
    },
    dispose: cancelTimer,
  };
}
